import { IAddScenario, IScenarioReferences, IAddScenarioCategories, ICatFinancialConstraints } from "types/addScenario";
import { Folder } from "types/folder";
import { Scenario } from "types/scenario";
import { ProductsV2Data, Product } from "types/result";
// eslint-disable-next-line import/no-extraneous-dependencies
import { v4 as uuidv4 } from "uuid";
import { minMaxPairs } from "constants/addScenarioDatas";

const getValue = <T>(value: T | undefined | null, defaultValue: T): T => value ?? defaultValue;

export const getDefaultScenarioData = ({
  folderData,
  productsData,
  scenarioData,
  isCopy,
}: {
  folderData: Folder;
  productsData: ProductsV2Data;
  scenarioData?: Scenario;
  isCopy?: boolean;
}) => {
  const subCategories = productsData.categoryList;

  const industryMovement = {
    1: "Yes",
    0: "No",
  };

  const subCatFinConstraints = subCategories.map((category) => ({
    category,
    netSales: "-",
    grossProfit: "-",
    volume: "-",
    minNetRevPerMktUnit: "-",
    maxNetRevPerMktUnit: "-",
  }));

  const scenario: IAddScenario = {
    scenarioId: getValue(scenarioData?.scenarioId, null),
    scenarioType: getValue(scenarioData?.scenarioType, null),
    name: getValue(scenarioData?.name, null),
    isCopy: getValue(isCopy, false),
    description: getValue(scenarioData?.description, null),
    buSizeFrequencies: null,
    scenarioFolder: {
      name: getValue(folderData?.name, null),
      folderId: getValue(folderData?.folderId, null),
      market: getValue(folderData?.market, null),
    },
    optTarget: getValue(scenarioData?.maximizationGoal, "cpg_profit"),
    financialConstraints: {
      catFinConstraints: {
        minNetSales: getValue(scenarioData?.netSales, 100),
        maxNetSales: getValue(scenarioData?.maxNetSales, 200),
        minGrossProfit: getValue(scenarioData?.grossMargin, 100),
        maxGrossProfit: getValue(scenarioData?.maxGrossMargin, 200),
        minVolume: getValue(scenarioData?.volume, 100),
        maxVolume: getValue(scenarioData?.maxVolume, 200),
        minNetRevenue: getValue(scenarioData?.minNetRevPerMktUnit, 100),
        maxNetRevenue: getValue(scenarioData?.maxNetRevPerMktUnit, 200),
        minMarketShare: getValue(scenarioData?.marketShare, 0),
        maxMarketShare: getValue(scenarioData?.maxMarketShare, 200),
      },
      occasions: {
        takeHome: false,
        multiPack: false,
        singleServe: false,
      },
      subCatFinConstraints: [],
      pricingConstraints: {
        inputPricingConstraintsId: isCopy
          ? null
          : getValue(scenarioData?.inputPricingConstraints?.inputPricingConstraintsId, null),
        brandSize: getValue(scenarioData?.inputPricingConstraints?.linePricingBrandSize, true),
        incentiveCurves: getValue(scenarioData?.inputPricingConstraints?.incentivesCurvesMinGap, 5),
        includeIncentiveCurves: getValue(scenarioData?.inputPricingConstraints?.includeIncentiveCurves, true),
      },
    },
    isWeightChanges: getValue(scenarioData?.isWeightChange, false),
    isEmailNotification: getValue(scenarioData?.isEmailNotification, false),
    industry: getValue(industryMovement[scenarioData?.competitorResponse], null),
    scenarioCat: {},
    references: {
      referenceFinancialConstraints: {
        netSales: "-",
        grossProfit: "-",
        volume: "-",
        minNetRevPerMktUnit: "-",
        maxNetRevPerMktUnit: "-",
        pepMktShr: "-",
      },
      subCategoryFinancialConstraints: subCatFinConstraints,
    },
    pricingGroups: getValue(productsData?.pricingGroups, []),
  };
  getDefaultDataV2(productsData, scenarioData, scenario, isCopy);

  return scenario;
};

const getInputFinancialConstraints = (scenario: IAddScenario, scenarioData: Scenario, isCopy: boolean) => {
  const inputFinConst = scenario?.financialConstraints?.subCatFinConstraints?.reduce((acc, curr) => {
    acc[curr.subCategory] = {};
    return acc;
  }, {});
  scenarioData?.inputFinancialConstraints?.forEach((finCtx) => {
    inputFinConst[finCtx.subCategory] = finCtx;
  });

  scenario?.financialConstraints?.subCatFinConstraints?.forEach((subFinCtx, idx) => {
    scenario.financialConstraints.subCatFinConstraints[idx].inputFinancialConstraintsId = isCopy
      ? null
      : Number(getValue(inputFinConst[subFinCtx.subCategory].inputFinancialConstraintsId, null));
    scenario.financialConstraints.subCatFinConstraints[idx].minGrossMargin =
      Number(getValue(inputFinConst[subFinCtx.subCategory].minGrossMargin, 0)) * 100;
    scenario.financialConstraints.subCatFinConstraints[idx].minNetSales =
      Number(getValue(inputFinConst[subFinCtx.subCategory].minNetSales, 0)) * 100;
    scenario.financialConstraints.subCatFinConstraints[idx].minVolume =
      Number(getValue(inputFinConst[subFinCtx.subCategory].minVolume, 0)) * 100;

    if (inputFinConst[subFinCtx.subCategory].occasion?.toLowerCase() === "multipack") {
      scenario.financialConstraints.occasions.multiPack = true;
    }
    if (inputFinConst[subFinCtx.subCategory].occasion?.toLowerCase() === "take home") {
      scenario.financialConstraints.occasions.takeHome = true;
    }
    if (inputFinConst[subFinCtx.subCategory].occasion?.toLowerCase() === "single serve") {
      scenario.financialConstraints.occasions.singleServe = true;
    }
  });
};

const getVisualPriceCat = (scenarioData: Scenario, isCopy: boolean) => {
  const visualPriceCat = {};
  scenarioData?.inputBUSizePricingConstraints?.forEach((priceCat) => {
    if (!visualPriceCat[priceCat.subcategory]) {
      visualPriceCat[priceCat.subcategory] = {};
      visualPriceCat[priceCat?.subcategory][priceCat?.buSizeName] = {};
      visualPriceCat[priceCat?.subcategory][priceCat?.buSizeName] = { ...priceCat };
      visualPriceCat[priceCat?.subcategory][priceCat?.buSizeName]["include"] = true;
    } else {
      if (!visualPriceCat[priceCat?.subcategory][priceCat?.buSizeName]) {
        visualPriceCat[priceCat?.subcategory][priceCat?.buSizeName] = { ...priceCat };
        visualPriceCat[priceCat?.subcategory][priceCat?.buSizeName]["include"] = true;
      } else {
        visualPriceCat[priceCat?.subcategory][priceCat?.buSizeName] = {
          ...visualPriceCat[priceCat?.subcategory][priceCat?.buSizeName],
          ...priceCat,
        };
        visualPriceCat[priceCat?.subcategory][priceCat?.buSizeName]["include"] = true;
      }
    }
  });

  scenarioData?.inputBUSizeWeightConstraints?.forEach((weightOutData) => {
    if (!visualPriceCat[weightOutData.subcategory]) {
      visualPriceCat[weightOutData.subcategory] = {};
      visualPriceCat[weightOutData?.subcategory][weightOutData?.buSizeName] = {};
      visualPriceCat[weightOutData?.subcategory][weightOutData?.buSizeName]["weightOut"] = weightOutData.weightChange;
      visualPriceCat[weightOutData?.subcategory][weightOutData?.buSizeName]["weightId"] = isCopy
        ? null
        : weightOutData.weightId;
    } else {
      if (!visualPriceCat[weightOutData?.subcategory][weightOutData?.buSizeName]) {
        visualPriceCat[weightOutData?.subcategory][weightOutData?.buSizeName] = {};
        visualPriceCat[weightOutData?.subcategory][weightOutData?.buSizeName]["weightOut"] = weightOutData.weightChange;
        visualPriceCat[weightOutData?.subcategory][weightOutData?.buSizeName]["weightId"] = isCopy
          ? null
          : weightOutData.weightId;
      } else {
        visualPriceCat[weightOutData?.subcategory][weightOutData?.buSizeName]["weightOut"] = weightOutData.weightChange;
        visualPriceCat[weightOutData?.subcategory][weightOutData?.buSizeName]["weightId"] = isCopy
          ? null
          : weightOutData.weightId;
      }
    }
  });

  return visualPriceCat;
};

const updateBuSizeFrequencies = (
  buSizeFrequencies: Record<string, any>,
  item: any,
  visualPriceCat: any,
  defaultMin: number,
  defaultMax: number,
) => {
  if (!buSizeFrequencies[item?.buSize]) {
    buSizeFrequencies[item?.buSize] = { freq: 0, min: defaultMin, max: defaultMax, include: false };
  }

  buSizeFrequencies[item?.buSize].freq += 1;

  buSizeFrequencies[item?.buSize].min =
    buSizeFrequencies[item?.buSize].min === defaultMin
      ? getValue(visualPriceCat?.[item.category]?.[item.buSize]?.minPrice, defaultMin)
      : buSizeFrequencies[item?.buSize].min;

  buSizeFrequencies[item?.buSize].max =
    buSizeFrequencies[item?.buSize].max === defaultMax
      ? getValue(visualPriceCat?.[item.category]?.[item.buSize]?.maxPrice, defaultMax)
      : buSizeFrequencies[item?.buSize].max;

  buSizeFrequencies[item?.buSize].include =
    buSizeFrequencies[item?.buSize].include === false
      ? visualPriceCat?.[item.category]?.[item.buSize]?.["include"] === true
      : buSizeFrequencies[item?.buSize].include;
};

const processScenarioItem = (
  scenario: any,
  buSizeFrequencies: any,
  visualPriceCat: any,
  item: any,
  defaultMin: number,
  defaultMax: number,
) => {
  updateBuSizeFrequencies(buSizeFrequencies, item, visualPriceCat, defaultMin, defaultMax);

  if (!scenario.scenarioCat[item.category]) {
    scenario.scenarioCat[item.category] = {
      includedTotal: visualPriceCat?.[item.category] ? Object.keys(visualPriceCat?.[item?.category]).length : 0,
      category: item.category,
      parentMin: defaultMin,
      parentMax: defaultMax,
      parentMinValError: null,
      parentMaxValError: null,
      subCats: [],
    };
  }

  const existingSubCat = scenario?.scenarioCat[item.category]?.subCats?.find(
    (subCat: any) => subCat.buSize === item.buSize,
  );

  if (!existingSubCat) {
    scenario.scenarioCat[item.category].subCats.push({
      productId: visualPriceCat?.[item.category]?.[item.buSize]?.pricingId || item.productId || uuidv4(),
      pricingId: getValue(visualPriceCat?.[item.category]?.[item.buSize]?.pricingId, null),
      weightId: getValue(visualPriceCat?.[item.category]?.[item.buSize]?.weightId, null),
      brand: item.brand,
      brandUnit: item.brandUnit,
      buSize: item.buSize,
      linePriceGroup: getValue(visualPriceCat?.[item.category]?.[item.buSize]?.linePricingGroup, null),
      weightOut: getValue(visualPriceCat?.[item.category]?.[item.buSize]?.["weightOut"], 0),
      include: buSizeFrequencies[item?.buSize].include,
      Minimum: getValue(buSizeFrequencies[item?.buSize].min, defaultMin),
      Maximum: getValue(buSizeFrequencies[item?.buSize].max, defaultMax),
      minValError: null,
      maxValError: null,
      isPepsico: item.isPepsico,
      actualPackSize: item.actualPackSize,
    });
  }
};

const getDefaultDataV2 = (
  productsData: ProductsV2Data,
  scenarioData: Scenario,
  scenario: IAddScenario,
  isCopy?: boolean,
) => {
  const buSizeFrequencies: Record<string, { freq: number; min: number; max: number; include: boolean }> = {};

  const subCatFinConstraints = productsData?.categoryList?.map((category) => ({
    inputFinancialConstraintsId: null,
    subCategory: category,
    minGrossMargin: 0,
    minNetSales: 0,
    minVolume: 0,
    maxNetRevenue: 200,
  }));
  const isFLUS = scenario?.scenarioFolder?.market === "FLUS";
  // Assign subCatFinConstraints to scenario
  scenario.financialConstraints.subCatFinConstraints = subCatFinConstraints;

  getInputFinancialConstraints(scenario, scenarioData, isCopy);

  const visualPriceCat = getVisualPriceCat(scenarioData, isCopy);

  if (productsData?.products?.length > 0) {
    productsData.products.forEach((item) => {
      const minDefaultPrice = isFLUS ? 0 : item.srpPriceSku;
      const maxDefaultPrice = isFLUS ? 12 : item.srpPriceSku;

      processScenarioItem(scenario, buSizeFrequencies, visualPriceCat, item, minDefaultPrice, maxDefaultPrice);
    });
  }
  scenario.buSizeFrequencies = buSizeFrequencies;
};

export const getDefaultData = (
  products: Product[],
  scenarioData: Scenario,
  scenario: IAddScenario,
  isCopy?: boolean,
) => {
  const buSizeFrequencies: Record<string, { freq: number; min: number; max: number; include: boolean }> = {};

  const subCats = [
    "Potato Chips",
    "Unflavored Tortilla Chips",
    "Flavored Tortilla Chips",
    "Multi Packs",
    "SINGLE SERVE",
  ];

  const subCatFinConstraints = subCats.map((category) => ({
    inputFinancialConstraintsId: null,
    subCategory: category,
    minGrossMargin: 0,
    minNetSales: 0,
    minVolume: 0,
    maxNetRevenue: 200,
  }));

  scenario.financialConstraints.subCatFinConstraints = subCatFinConstraints;

  getInputFinancialConstraints(scenario, scenarioData, isCopy);

  const visualPriceCat = getVisualPriceCat(scenarioData, isCopy);

  if (products.length > 0) {
    products.forEach((item) => {
      if (item.isPepsico !== false) {
        if (item.buSize && item.tier === "bu_size") {
          processScenarioItem(scenario, buSizeFrequencies, visualPriceCat, item, 0, 12);
        }
      }
    });
  }

  scenario.buSizeFrequencies = buSizeFrequencies;
};
// Helper function to process inputScope
const getInputScope = (scenarioInfo: IAddScenario) => {
  const inputScope = [];
  const added = new Set();

  for (const category in scenarioInfo.scenarioCat) {
    scenarioInfo.scenarioCat[category].subCats.forEach((subCat) => {
      if (subCat.include && !added.has(subCat.productId)) {
        added.add(subCat.productId);
        inputScope.push({ productBrandSize: subCat.buSize });
      }
    });
  }

  return inputScope;
};

// Helper function to process pricing constraints
const getPricingConstraints = (scenarioInfo: IAddScenario, isMarketPMF: boolean, isTestInputConfig = false) => {
  const constraints = [];

  for (const category in scenarioInfo.scenarioCat) {
    scenarioInfo.scenarioCat[category].subCats.forEach((subCat) => {
      if (subCat.include || isTestInputConfig) {
        constraints.push({
          pricingId: subCat.pricingId,
          buSizeName: subCat.buSize,
          subcategory: category,
          minPrice: !subCat.include && !isMarketPMF ? 0 : subCat.Minimum,
          maxPrice: !subCat.include && !isMarketPMF ? 0 : subCat.Maximum,
          linePricingGroup: subCat.linePriceGroup,
          isIncluded: subCat.include,
        });
      }
    });
  }

  return constraints;
};

// Helper function to process weight constraints
const getWeightConstraints = (scenarioInfo: IAddScenario) => {
  const constraints = [];

  for (const category in scenarioInfo.scenarioCat) {
    scenarioInfo.scenarioCat[category].subCats.forEach((subCat) => {
      if (scenarioInfo.isWeightChanges && subCat.weightOut !== 0) {
        constraints.push({
          weightId: subCat.weightId,
          weightChange: subCat.weightOut,
          subcategory: category,
          buSizeName: subCat.buSize,
        });
      }
    });
  }

  return constraints;
};

// Helper function to determine the occasion
const getOccasion = (item: any, scenarioInfo: IAddScenario) => {
  if (
    scenarioInfo.financialConstraints.occasions.takeHome &&
    item.subCategory !== "MULTIPACK" &&
    item.subCategory !== "SINGLE SERVE"
  ) {
    return "Take home";
  }
  if (scenarioInfo.financialConstraints.occasions.multiPack && item.subCategory === "MULTIPACK") {
    return "Multipack";
  }
  if (scenarioInfo.financialConstraints.occasions.singleServe && item.subCategory === "SINGLE SERVE") {
    return "Single Serve";
  }
  return "";
};

// Helper function to process financial constraints
const getFinancialConstraints = (scenarioInfo: IAddScenario) => {
  const constraints = [];

  scenarioInfo.financialConstraints.subCatFinConstraints.forEach((item) => {
    const occasion = getOccasion(item, scenarioInfo);
    constraints.push({
      inputFinancialConstraintsId: item.inputFinancialConstraintsId > 0 ? item.inputFinancialConstraintsId : null,
      subCategory: item.subCategory,
      minNetSales: item.minNetSales / 100,
      minGrossMargin: item.minGrossMargin / 100,
      minVolume: item.minVolume / 100,
      maxNetRevenue: item.maxNetRevenue,
      occasion,
    });
  });

  return constraints;
};

// Helper function to set optimization targets
const setOptimizationTargets = (data: Scenario, scenarioInfo: IAddScenario) => {
  if (scenarioInfo.optTarget !== "cpg_revenue") {
    data.netSales = scenarioInfo.financialConstraints.catFinConstraints.minNetSales;
  }
  if (scenarioInfo.optTarget !== "cpg_profit") {
    data.grossMargin = scenarioInfo.financialConstraints.catFinConstraints.minGrossProfit;
  }
  if (scenarioInfo.optTarget !== "cpg_volume") {
    data.volume = scenarioInfo.financialConstraints.catFinConstraints.minVolume;
  }
};

export const prepareApiData = (scenarioInfo: IAddScenario, isMarketPMF, isTestInputConfig = false) => {
  const industryMovement = {
    "Industry moves 100%": 1,
    "Industry moves 50%": 0.5,
    "No movement": 0,
    Yes: 1,
    No: 0,
  };

  const inputScope = getInputScope(scenarioInfo);
  const inputBUSizePricingConstraints = getPricingConstraints(scenarioInfo, isMarketPMF, isTestInputConfig);
  const inputBUSizeWeightConstraints = getWeightConstraints(scenarioInfo);
  const inputFinancialConstraints = getFinancialConstraints(scenarioInfo);

  const data: Scenario = {
    scenarioId: scenarioInfo?.scenarioId || null,
    scenarioType: scenarioInfo?.scenarioType || "optimized",
    name: scenarioInfo.name,
    isCopy: scenarioInfo?.isCopy,
    description: scenarioInfo.description,
    folder: {
      name: scenarioInfo.scenarioFolder.name,
      folderId: scenarioInfo.scenarioFolder.folderId,
    },
    maximizationGoal: scenarioInfo.optTarget,
    minNetRevPerMktUnit: scenarioInfo.financialConstraints.catFinConstraints.minNetRevenue,
    maxNetRevPerMktUnit: scenarioInfo.financialConstraints.catFinConstraints.maxNetRevenue,
    marketShare: scenarioInfo.financialConstraints.catFinConstraints.minMarketShare,
    maxMarketShare: scenarioInfo.financialConstraints.catFinConstraints.maxMarketShare,
    volume: scenarioInfo.financialConstraints.catFinConstraints.minVolume,
    maxVolume: scenarioInfo.financialConstraints.catFinConstraints.maxVolume,
    netSales: scenarioInfo.financialConstraints.catFinConstraints.minNetSales,
    maxNetSales: scenarioInfo.financialConstraints.catFinConstraints.maxNetSales,
    grossMargin: scenarioInfo.financialConstraints.catFinConstraints.minGrossProfit,
    maxGrossMargin: scenarioInfo.financialConstraints.catFinConstraints.maxGrossProfit,
    competitorResponse: industryMovement[scenarioInfo.industry],
    isWeightChange: scenarioInfo.isWeightChanges,
    inputFinancialConstraints,
    inputPricingConstraints: {
      inputPricingConstraintsId: scenarioInfo.financialConstraints.pricingConstraints.inputPricingConstraintsId,
      minPriceChange: 0,
      maxPriceChange: 0,
      stepPriceChange: 0,
      linePricingBrandSize: scenarioInfo.financialConstraints.pricingConstraints.brandSize,
      incentivesCurvesMinGap: scenarioInfo.financialConstraints.pricingConstraints.incentiveCurves,
      includeIncentiveCurves: scenarioInfo.financialConstraints.pricingConstraints.includeIncentiveCurves,
    },
    inputScope,
    inputBUSizePricingConstraints,
    inputBUSizeWeightConstraints,
    isEmailNotification: scenarioInfo.isEmailNotification,
  };

  setOptimizationTargets(data, scenarioInfo);

  return data;
};

export const isNumeric = (value: any): boolean => !Number.isNaN(parseFloat(value)) && isFinite(value);

export const isEmpty = (value: any): boolean => value === "" || value === null || value === undefined;

export const firstLetterCapital = (word: string | number) => {
  word = word.toString();
  const words = word.split(" ");
  const possibleSizes = new Set(["xxl", "xl", "lg"]);
  let res = "";
  for (let i = 0; i < words.length; i++) {
    let temp = words[i].toLowerCase();
    if (possibleSizes.has(temp)) {
      temp = words[i].toUpperCase();
    } else {
      temp = temp.charAt(0).toUpperCase() + temp.slice(1);
    }
    res += temp + " ";
  }
  return res;
};

const formatNumber = (number: number) => {
  if (Math.abs(number) >= 1e9) {
    return `$${(number / 1e9).toFixed(2)}B`;
  } else if (Math.abs(number) >= 1e6) {
    return `$${(number / 1e6).toFixed(2)}M`;
  } else {
    return number.toString();
  }
};

export const getUpdateScenarioReferences = (
  defaultConstrCurrScenario: IScenarioReferences,
  constrFromRefScenario: IScenarioReferences,
) => {
  try {
    const temp = { ...constrFromRefScenario };
    const newReferences: IScenarioReferences = { ...defaultConstrCurrScenario };

    Object.keys(temp).forEach((key) => {
      if (temp[key] !== null) {
        if (key === "referenceFinancialConstraints") {
          Object.keys(temp[key]).forEach((value) => {
            if (temp[key][value] !== null) {
              newReferences.referenceFinancialConstraints[value] = formatNumber(temp[key][value]);
            }
          });
        } else if (key === "subCategoryFinancialConstraints") {
          temp[key].forEach((category, index) => {
            if (category && typeof category === "object") {
              // Create a new object to clone the category
              const newCategory = {
                category: "", // Provide a default value for category
                grossProfit: "-",
                netSales: "-",
                volume: "-",
                minNetRevPerMktUnit: "-",
                maxNetRevPerMktUnit: "-",
              };

              // Iterate through the properties of the category object
              Object.keys(category).forEach((value) => {
                if (category[value] !== null) {
                  if (value === "category") {
                    // Copy the "category" property as-is
                    newCategory[value] = category[value];
                  } else {
                    // Format and copy other properties
                    newCategory[value] = formatNumber(category[value]);
                  }
                }
              });

              // Add the cloned category object to newScenario.references.subCategoryFinancialConstraints
              newReferences.subCategoryFinancialConstraints[index] = newCategory;
            }
          });
        }
      }
    });

    return newReferences;
  } catch (error) {
    console.log(error);
  }
};

export const findBuSizeWo = (scenarioCat: Record<string, IAddScenarioCategories>) => {
  const buSizeWO: string[] = [];
  Object.keys(scenarioCat).forEach((indvCat) => {
    scenarioCat[indvCat].subCats.forEach((subCat) => {
      if (subCat.weightOut) {
        buSizeWO.push(subCat.buSize);
      }
    });
  });
  return buSizeWO;
};

export const hasSubCatBuSizeWO = (weightOut: number, buSize: string, buSizeWO: string[]) => {
  return Boolean(weightOut || buSizeWO.includes(buSize));
};

export const validateMinMaxConstraints = (name: string, value: number, allConstraints: ICatFinancialConstraints) => {
  const errors: Record<string, string> = {};

  // Validate against its paired field
  const pairedField = minMaxPairs[name];
  if (!pairedField) return errors;

  if (pairedField) {
    const isMinField = name.startsWith("min");
    const minValue = isMinField ? value : allConstraints[pairedField];
    const maxValue = isMinField ? allConstraints[pairedField] : value;

    if (minValue !== undefined && maxValue !== undefined && minValue !== "" && maxValue !== "" && minValue > maxValue) {
      if (isMinField) {
        errors[name] = `Min value cannot be greater than max value (${maxValue}%)`;
      } else {
        errors[name] = `Max value cannot be less than min value (${minValue}%)`;
      }
    }
  }

  return errors;
};

export const displayValue = (val: string | number | undefined | null): string => {
  if (val === undefined || val === null || val === "") return "-";
  return String(val);
};

export const showConstraint = (
  isMarketPMF: boolean,
  optTarget: string | undefined,
  optTargetMap: Record<string, string>,
  target: string,
) => {
  return !isMarketPMF || (optTarget && optTargetMap[optTarget]?.toLowerCase() !== target.toLowerCase());
};
